import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Loader2,
  Clock, MapPin, Trash2, Check, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Working hours window
const HOUR_START = 7;
const HOUR_END = 21;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
const ROW_PX = 56; // px per hour

const STATUS_META = {
  scheduled: { label: "Geplant", color: "bg-cyan-500", text: "text-cyan-50" },
  done:      { label: "Erledigt", color: "bg-green-500", text: "text-green-50" },
  cancelled: { label: "Abgesagt", color: "bg-slate-400", text: "text-slate-50" },
  no_show:   { label: "No-Show", color: "bg-amber-500", text: "text-amber-50" },
};

// Owner color palette (deterministic by owner_id hash)
const OWNER_COLORS = [
  "from-cyan-500 to-cyan-600",
  "from-violet-500 to-violet-600",
  "from-amber-500 to-amber-600",
  "from-emerald-500 to-emerald-600",
  "from-rose-500 to-rose-600",
  "from-indigo-500 to-indigo-600",
];
function hashColor(id) {
  if (!id) return OWNER_COLORS[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return OWNER_COLORS[Math.abs(h) % OWNER_COLORS.length];
}

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}
function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function sameDay(a, b) {
  return a.toDateString() === b.toDateString();
}
function fmtDay(d) {
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
}
function fmtTime(d) {
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [view, setView] = useState("week"); // week | day
  const [anchor, setAnchor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const isAdmin = user?.role === "admin";

  // Compute visible date range
  const range = useMemo(() => {
    if (view === "day") {
      const start = new Date(anchor);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { from: start, to: end, days: [start] };
    }
    const start = startOfWeek(anchor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return { from: start, to: addDays(start, 7), days };
  }, [anchor, view]);

  // Load users (for admin filter)
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => setUsers(r.data?.items || r.data || []))
      .catch(() => setUsers([]));
  }, [isAdmin]);

  // Load appointments for range
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_at: range.from.toISOString(),
        to_at: range.to.toISOString(),
      };
      if (isAdmin && ownerFilter !== "all") params.owner_id = ownerFilter;
      const res = await axios.get(`${API}/appointments`, { params, withCredentials: true });
      setItems(res.data?.items || []);
    } catch {
      toast.error("Termine konnten nicht geladen werden");
      setItems([]);
    } finally { setLoading(false); }
  }, [range, isAdmin, ownerFilter]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const goPrev = () => setAnchor(addDays(anchor, view === "day" ? -1 : -7));
  const goNext = () => setAnchor(addDays(anchor, view === "day" ? 1 : 7));
  const goToday = () => setAnchor(new Date());

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { status }, { withCredentials: true });
      toast.success(STATUS_META[status]?.label + " gesetzt");
      setSelected(null);
      loadAppointments();
    } catch (e) {
      toast.error("Status konnte nicht aktualisiert werden");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success("Termin gelöscht");
      setSelected(null);
      loadAppointments();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const itemsForDay = (day) => items.filter(a => sameDay(new Date(a.start_at), day));

  // Calculate position/height of a block within an hour grid
  const blockStyle = (a) => {
    const s = new Date(a.start_at);
    const e = new Date(a.end_at);
    const minutesFromTop = (s.getHours() - HOUR_START) * 60 + s.getMinutes();
    const durationMin = (e - s) / 60_000;
    return {
      top: `${(minutesFromTop / 60) * ROW_PX}px`,
      height: `${Math.max(20, (durationMin / 60) * ROW_PX - 2)}px`,
    };
  };

  return (
    <>
      <div className="max-w-[1400px] mx-auto" data-testid="appointments-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <CalendarIcon size={26} /> Termine
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isAdmin ? "Alle Termine im Team" : "Deine vereinbarten Termine"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && users.length > 0 && (
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="owner-filter">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Vertriebler</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex bg-muted rounded-sm p-0.5">
              <button
                onClick={() => setView("day")}
                data-testid="view-day-btn"
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                  view === "day" ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >Tag</button>
              <button
                onClick={() => setView("week")}
                data-testid="view-week-btn"
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                  view === "week" ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >Woche</button>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-4">
          <Button size="sm" variant="outline" onClick={goPrev} className="h-9 rounded-sm" data-testid="prev-btn"><ChevronLeft size={16}/></Button>
          <Button size="sm" variant="outline" onClick={goToday} className="h-9 rounded-sm text-xs" data-testid="today-btn">Heute</Button>
          <Button size="sm" variant="outline" onClick={goNext} className="h-9 rounded-sm" data-testid="next-btn"><ChevronRight size={16}/></Button>
          <span className="ml-3 text-sm font-semibold">
            {view === "day"
              ? anchor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
              : `${range.days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${range.days[6].toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`}
          </span>
          {loading && <Loader2 size={14} className="animate-spin text-muted-foreground ml-2"/>}
          <span className="ml-auto text-xs text-muted-foreground">{items.length} Termin{items.length !== 1 ? "e" : ""}</span>
        </div>

        {/* Calendar Grid */}
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <div className="grid" style={{ gridTemplateColumns: `60px repeat(${range.days.length}, 1fr)` }}>
            {/* Header row */}
            <div className="border-b border-border bg-muted/30"></div>
            {range.days.map((d) => {
              const isToday = sameDay(d, new Date());
              return (
                <div key={d.toISOString()} className={`border-b border-l border-border px-3 py-2.5 text-center ${isToday ? "bg-cyan-500/5" : "bg-muted/30"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {d.toLocaleDateString("de-DE", { weekday: "short" })}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? "text-cyan-500" : ""}`}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}

            {/* Time labels column */}
            <div className="relative border-r border-border" style={{ height: `${HOURS.length * ROW_PX}px` }}>
              {HOURS.map((h) => (
                <div key={h} className="absolute w-full text-right pr-2 text-[10px] text-muted-foreground -translate-y-1/2"
                     style={{ top: `${(h - HOUR_START) * ROW_PX}px` }}>
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            {range.days.map((day) => {
              const dayItems = itemsForDay(day);
              const isToday = sameDay(day, new Date());
              return (
                <div key={day.toISOString()} className={`relative border-l border-border ${isToday ? "bg-cyan-500/[0.02]" : ""}`}
                     style={{ height: `${HOURS.length * ROW_PX}px` }}>
                  {/* Hour grid lines */}
                  {HOURS.map((h, i) => (
                    <div key={h} className="absolute inset-x-0 border-t border-border/50" style={{ top: `${i * ROW_PX}px`, height: `${ROW_PX}px` }} />
                  ))}
                  {/* Half-hour subtle lines */}
                  {HOURS.map((h, i) => (
                    <div key={`half-${h}`} className="absolute inset-x-0 border-t border-dashed border-border/30" style={{ top: `${(i + 0.5) * ROW_PX}px` }} />
                  ))}
                  {/* Now line */}
                  {isToday && (() => {
                    const now = new Date();
                    const minFromTop = (now.getHours() - HOUR_START) * 60 + now.getMinutes();
                    if (minFromTop < 0 || minFromTop > HOURS.length * 60) return null;
                    return (
                      <div className="absolute inset-x-0 border-t-2 border-red-500 z-20"
                           style={{ top: `${(minFromTop / 60) * ROW_PX}px` }}>
                        <span className="absolute -left-1 -top-1.5 w-2.5 h-2.5 rounded-full bg-red-500"/>
                      </div>
                    );
                  })()}
                  {/* Appointments */}
                  {dayItems.map(a => {
                    const colorClass = a.status === "cancelled"
                      ? "from-slate-400 to-slate-500"
                      : a.status === "done"
                      ? "from-green-500 to-green-600"
                      : a.status === "no_show"
                      ? "from-amber-500 to-amber-600"
                      : hashColor(a.owner_id);
                    return (
                      <button
                        key={a.id}
                        onClick={() => setSelected(a)}
                        data-testid={`appt-block-${a.id}`}
                        className={`absolute left-1 right-1 rounded-md p-1.5 text-[10px] text-white text-left bg-gradient-to-br ${colorClass} hover:brightness-110 transition-all shadow-sm overflow-hidden`}
                        style={blockStyle(a)}
                      >
                        <div className="font-bold leading-tight truncate">{a.title}</div>
                        <div className="opacity-90 text-[9px] mt-0.5 leading-tight truncate">
                          {fmtTime(new Date(a.start_at))}–{fmtTime(new Date(a.end_at))}
                        </div>
                        {a.company_name && view === "day" && (
                          <div className="opacity-80 text-[9px] truncate">{a.company_name}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend (admin) */}
        {isAdmin && users.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-3 text-[11px]">
            {[...new Set(items.map(i => i.owner_id))].map(oid => {
              const u = users.find(x => x.id === oid);
              if (!u) return null;
              return (
                <div key={oid} className="flex items-center gap-1.5">
                  <span className={`w-3 h-3 rounded-sm bg-gradient-to-br ${hashColor(oid)}`}/>
                  <span>{u.name || u.email}</span>
                </div>
              );
            })}
          </div>
        )}

        {items.length === 0 && !loading && (
          <div className="text-center text-sm text-muted-foreground mt-8 py-12 border border-dashed border-border rounded-md">
            <CalendarIcon size={32} className="mx-auto mb-3 text-muted-foreground/40"/>
            Keine Termine in diesem Zeitraum.<br/>
            <span className="text-xs">Erstelle Termine direkt aus einem Lead heraus.</span>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selected && (
        <AppointmentDetailPanel
          appt={selected}
          onClose={() => setSelected(null)}
          onStatus={setStatus}
          onDelete={handleDelete}
          isAdmin={isAdmin}
          currentUserId={user?.id}
        />
      )}
    </>
  );
}

function AppointmentDetailPanel({ appt, onClose, onStatus, onDelete, isAdmin, currentUserId }) {
  const canEdit = isAdmin || appt.owner_id === currentUserId;
  const start = new Date(appt.start_at);
  const end = new Date(appt.end_at);
  const meta = STATUS_META[appt.status] || STATUS_META.scheduled;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-card border border-border rounded-md shadow-xl w-full max-w-md p-5 space-y-4"
           data-testid="appt-detail-panel">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${meta.color} ${meta.text}`}>
                {meta.label}
              </span>
              {appt.company_name && (
                <span className="text-[11px] text-muted-foreground">{appt.company_name}</span>
              )}
            </div>
            <h2 className="text-lg font-bold mt-1.5 leading-tight">{appt.title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-muted text-muted-foreground"><X size={16}/></button>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock size={14}/>
            <span>
              {start.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
              {" · "}
              {fmtTime(start)} – {fmtTime(end)} ({appt.duration_min} Min)
            </span>
          </div>
          {appt.location && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin size={14}/> {appt.location}
            </div>
          )}
          {appt.owner_name && (
            <div className="text-xs text-muted-foreground">Vertriebler: <strong>{appt.owner_name}</strong></div>
          )}
          {appt.notes && (
            <div className="bg-muted/50 rounded-sm p-2.5 text-xs whitespace-pre-wrap">{appt.notes}</div>
          )}
        </div>

        {canEdit && appt.status === "scheduled" && (
          <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => onStatus(appt.id, "done")} className="rounded-sm gap-1.5" data-testid="appt-mark-done">
              <Check size={13}/> Erledigt
            </Button>
            <Button size="sm" variant="outline" onClick={() => onStatus(appt.id, "no_show")} className="rounded-sm gap-1.5" data-testid="appt-mark-noshow">
              <X size={13}/> No-Show
            </Button>
            <Button size="sm" variant="outline" onClick={() => onStatus(appt.id, "cancelled")} className="rounded-sm gap-1.5" data-testid="appt-mark-cancelled">
              Absagen
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(appt.id)} className="rounded-sm gap-1.5 ml-auto text-red-500 border-red-500/30 hover:bg-red-500/10" data-testid="appt-delete">
              <Trash2 size={13}/> Löschen
            </Button>
          </div>
        )}
        {canEdit && appt.status !== "scheduled" && (
          <div className="flex gap-2 pt-2 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => onStatus(appt.id, "scheduled")} className="rounded-sm gap-1.5" data-testid="appt-mark-scheduled">
              Wieder planen
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(appt.id)} className="rounded-sm gap-1.5 ml-auto text-red-500 border-red-500/30 hover:bg-red-500/10">
              <Trash2 size={13}/> Löschen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
